import React from 'react';
import { flushSync } from 'react-dom';

import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * The parts of the page the morph names (`data-art-morph`): the sheet's card and the rail it becomes, the Ingredients
 * heading and body in either, and the samples that arrive beside the rail. `MorphStyles` (art-style.styles.ts) gives
 * them their transition names while {@link MORPH_ROOT_CLASS} is on `<html>`, and only then.
 */
export const ART_MORPH = {
  card: 'card',
  head: 'head',
  body: 'body',
  samples: 'samples',
} as const;

/** On `<html>` for the length of the morph: it scopes the transition names and the pseudo-element animations. */
export const MORPH_ROOT_CLASS = 'ows-art-morph';

/** The View Transitions API as far as the morph uses it (TypeScript 5.3's DOM types predate it). */
type MorphTransition = { finished: Promise<void> };
type TransitionDocument = Document & { startViewTransition?: (update: () => void) => MorphTransition };

export interface IngredientsMorph {
  /** True only inside the commit the morph renders, so the split's own arrival (useSplitArrival) stands aside. */
  morphingRef: React.MutableRefObject<boolean>;
}

/**
 * A style's first definition landing while its ingredients sheet is open: the sheet turns into the studio's rail.
 *
 * The page holds the sheet open (and the split back) while `landing`; this then lets it go inside a view transition, so
 * the browser morphs between the two states: the Ingredients heading and body glide from the sheet to the rail, the
 * sheet's card shrinks into the rail's box and dissolves, the dim lifts and the samples slide in. Without the API, or
 * under reduced motion, it lets go at once and the split shows the way it always has.
 */
export const useIngredientsMorph = (landing: boolean, release: () => void): IngredientsMorph => {
  const reduced = useReducedMotion();
  const morphingRef = React.useRef(false);
  // Read when the transition calls back, a frame later: the page's latest release, not the one this effect saw.
  const releaseRef = React.useRef(release);
  releaseRef.current = release;

  React.useEffect(() => {
    if (!landing) return;
    const doc = document as TransitionDocument;
    if (reduced || typeof doc.startViewTransition !== 'function') {
      releaseRef.current();
      return;
    }
    const root = document.documentElement;
    root.classList.add(MORPH_ROOT_CLASS);
    // The browser captures the sheet as it is now, then calls back for the new state, which has to be in the DOM by
    // the time the callback returns: flushSync commits the split there and then.
    const transition = doc.startViewTransition(() => {
      morphingRef.current = true;
      try {
        flushSync(() => releaseRef.current());
      } finally {
        morphingRef.current = false;
      }
    });
    const done = (): void => root.classList.remove(MORPH_ROOT_CLASS);
    transition.finished.then(done, done);
  }, [landing, reduced]);

  return { morphingRef };
};
